import {
  useRef,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { getCroppedImg } from "src/Components/ImageCropp/getCroppedImage";
import { Box, Typography, Slider, Button } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteIcon from "@mui/icons-material/Delete";
import { Close } from "@benefit-systems/icons";
import __ from "src/utils/Translations";
import Cropper, { Area } from "react-easy-crop";
import emptyAvatar from "./Blank-Avatar.webp";

interface CroppieOptions {
  viewport: { width: number; height: number; type?: "square" | "circle" };
}

export interface ImageCroppRef {
  submitCrop: () => Promise<void>;
}

interface ImageCroppProps {
  onClose?: () => void;
  onSave: (croppedImage: string | Blob, format: "png" | "jpeg") => void;
  onDelete?: () => void;
  image?: string;
  outputSize?: { width: number; height: number };
  outputType?: "base64" | "blob";
  cropSize?: { width: number; height: number };
  croppieTitle?: string;
  croppieConfig: CroppieOptions;
}

const ImageCropp = forwardRef<ImageCroppRef, ImageCroppProps>(
  (
    {
      onClose,
      image,
      onSave,
      onDelete,
      outputType = "base64",
      cropSize = { width: 240, height: 135 },
      croppieTitle,
      croppieConfig,
      outputSize = { width: 1920, height: 1080 },
    },
    ref
  ) => {
    const [src, setSrc] = useState<string>(image || emptyAvatar);
    // eslint-disable-next-line id-length
    const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState<number>(1.1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(
      null
    );

    const isCircle = croppieConfig.viewport.type === "circle";

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const aspect = isCircle ? 1 : 16 / 9;

    const onCropComplete = useCallback((__: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    }, []);

    const onSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files?.[0];
        const reader = new FileReader();
        reader.addEventListener("load", () => {
          setSrc(reader.result?.toString() || "");
        });
        reader.readAsDataURL(file);
      }
    };

    const submitCrop = useCallback(async () => {
      if (!croppedAreaPixels || !src) return;

      const croppedCanvas = await getCroppedImg(
        src,
        croppedAreaPixels,
        outputSize,
        isCircle
      );

      if (!croppedCanvas) return;

      if (outputType === "blob") {
        croppedCanvas.toBlob((blob) => {
          if (blob) onSave(blob, "png");
        }, "image/png");
      } else {
        const base64Image = croppedCanvas.toDataURL("image/png");
        onSave(base64Image, "png");
      }
    }, [croppedAreaPixels, src, outputSize, isCircle, onSave, outputType]);

    useImperativeHandle(ref, () => ({
      submitCrop,
    }));

    useEffect(() => {
      setSrc(image || "");
    }, [image]);

    return (
      <Box>
        {croppieTitle && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "24px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                lineHeight: "20px",
                color: "#24272A",
                fontWeight: 800,
                textTransform: "uppercase",
              }}
            >
              {__(croppieTitle)}
            </Typography>
            <Close width={14} onClick={onClose} style={{ cursor: "pointer" }} />
          </Box>
        )}

        <Box
          sx={{
            position: "relative",
            width: croppieConfig.viewport.width,
            height: croppieConfig.viewport.height,
          }}
        >
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={isCircle ? "round" : "rect"}
            {...(!isCircle ? { cropSize } : {})}
            showGrid={false}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            restrictPosition={false}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Slider
            value={zoom}
            min={0.1}
            max={3}
            step={0.1}
            onChange={(__, zoomSize) => setZoom(zoomSize as number)}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png, image/jpeg"
            style={{ display: "none" }}
            onChange={onSelectFile}
          />
          <Box
            component="label"
            sx={{
              marginTop: 1,
              display: "flex",
              gap: "15px",
              width: "100%",
            }}
          >
            <Button
              variant="cropperButton"
              startIcon={<SyncIcon width={15} />}
              onClick={() => fileInputRef.current?.click()}
            >
              {__("Zmień zdjęcie")}
            </Button>
            <Button
              variant="cropperButton"
              onClick={() => {
                setSrc("");
                if (onDelete) onDelete();
              }}
              startIcon={<DeleteIcon width={15} />}
            >
              {__("Usuń zdjęcie")}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }
);

ImageCropp.displayName = "ImageCroppSO";

export default ImageCropp;
